import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Envelope, X } from '@phosphor-icons/react';

import { getEmailPreview } from '@/api/emaillog';
import { ApiError } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';

// EmailPreviewDialog re-renders a single core.email_log row through the
// server-side templates registry and shows the recipient exactly what
// was sent — subject + headers + HTML / plain-text body — in a Radix
// modal. The HTML variant renders inside a sandboxed iframe (srcdoc) so
// inline email styles don't leak into the SPA chrome and any rogue
// markup in user-supplied params can't escape the frame.
//
// The "View" button is only shown when the row's has_preview flag is
// true, so opening this dialog without a registered template is a
// caller bug — we still render a graceful "no preview" fallback in
// case the server's template registry drifts from the SPA's view of it.

export interface EmailPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  slug: string;
  token: string | null;
  messageId: string | null;
}

export function EmailPreviewDialog({
  open,
  onOpenChange,
  slug,
  token,
  messageId,
}: EmailPreviewDialogProps) {
  const { t } = useTranslation('common');
  const [tab, setTab] = useState<'html' | 'text'>('html');

  // Reset tab back to 'html' each time the dialog opens — operators
  // expect the rich variant first; plain text is the fallback.
  useEffect(() => {
    if (open) setTab('html');
  }, [open]);

  const previewQuery = useQuery({
    queryKey: ['email-preview', slug, messageId],
    queryFn: () => getEmailPreview(token, slug, messageId!),
    enabled: open && !!token && !!messageId,
    staleTime: 60_000,
  });

  const data = previewQuery.data;
  const hasHTML = !!data?.html_body;

  // Fall through to text when there's no HTML at all. Done inside an
  // effect because tab state is controlled and we don't want to flip it
  // before the data lands.
  useEffect(() => {
    if (data && !hasHTML) setTab('text');
  }, [data, hasHTML]);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[80] bg-foreground/40" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className="fixed left-1/2 top-1/2 z-[81] flex h-[88vh] max-h-[88vh] w-[min(48rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl bg-card text-foreground shadow-xl"
        >
          <header className="flex items-start gap-3 border-b border-border px-6 py-4">
            <span
              aria-hidden
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-pill bg-muted text-foreground/70"
            >
              <Envelope size={18} weight="duotone" />
            </span>
            <div className="flex-1 overflow-hidden">
              <DialogPrimitive.Title className="truncate font-heading text-lg font-bold leading-tight">
                {data?.subject || t('settings.log.email.preview.loadingTitle')}
              </DialogPrimitive.Title>
              {data && (
                <p className="mt-0.5 truncate font-mono text-xs text-muted-foreground">
                  {t('settings.log.email.preview.toLabel')}: {data.to_address}
                </p>
              )}
            </div>
            <DialogPrimitive.Close asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={t('cancel')}
              >
                <X weight="bold" />
              </Button>
            </DialogPrimitive.Close>
          </header>

          <div className="flex flex-1 flex-col overflow-hidden">
            {previewQuery.isPending && (
              <p className="px-6 py-6 text-sm text-muted-foreground">
                {t('settings.log.email.preview.loading')}
              </p>
            )}
            {previewQuery.isError && (
              <p className="px-6 py-6 text-sm text-foreground">
                {renderPreviewError(previewQuery.error, t)}
              </p>
            )}
            {data && (
              <Tabs
                value={tab}
                onValueChange={(v) => setTab(v as 'html' | 'text')}
                className="flex flex-1 flex-col overflow-hidden"
              >
                <div className="border-b border-border px-6 py-3">
                  <TabsList>
                    <TabsTrigger value="html" disabled={!hasHTML}>
                      {t('settings.log.email.preview.tabs.html')}
                    </TabsTrigger>
                    <TabsTrigger value="text">
                      {t('settings.log.email.preview.tabs.text')}
                    </TabsTrigger>
                  </TabsList>
                </div>
                <TabsContent
                  value="html"
                  className="flex-1 overflow-hidden bg-muted/30 p-4"
                >
                  <HTMLPreview html={data.html_body} />
                </TabsContent>
                <TabsContent
                  value="text"
                  className="flex-1 overflow-auto px-6 py-4"
                >
                  <pre className="whitespace-pre-wrap break-words font-mono text-xs text-foreground/80">
                    {data.text_body}
                  </pre>
                </TabsContent>
              </Tabs>
            )}
          </div>

          <footer className={cn(
            'flex items-center justify-between gap-3 border-t border-border px-6 py-3',
          )}>
            <span className="font-heading text-[0.7rem] uppercase tracking-wider text-muted-foreground">
              {data?.template_key && t('settings.log.email.preview.template', { key: data.template_key })}
            </span>
            <DialogPrimitive.Close asChild>
              <Button type="button" variant="outline">
                {t('common.close')}
              </Button>
            </DialogPrimitive.Close>
          </footer>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

// HTMLPreview renders the email body inside a sandboxed iframe. srcdoc
// gives the iframe its initial document without a network round-trip;
// the sandbox attribute (with no flags) strips scripts, top-navigation,
// form submission, popups, plugins, and same-origin access so a rogue
// param value can't break out into the SPA. The container fills the
// available height so long emails scroll inside the frame rather than
// stretching the dialog.
function HTMLPreview({ html }: { html: string }) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  // Wrap the email HTML in a minimal document so <meta charset> is
  // present (some templates omit it) and a default font matches the
  // dialog chrome.
  const doc = useMemo(() => wrapEmailDocument(html), [html]);

  return (
    <iframe
      ref={iframeRef}
      title="Email preview"
      sandbox=""
      srcDoc={doc}
      className="h-full w-full rounded-xl border border-border bg-white"
    />
  );
}

function wrapEmailDocument(html: string): string {
  // A <base> pointing at the SPA's own origin lets relative image URLs in
  // the email body — the club logo and any /storage/<key> block images —
  // resolve against the current environment instead of failing inside the
  // about:srcdoc iframe (which has no usable base of its own). /storage/ is
  // served same-origin, so this works in dev, staging, and prod alike
  // without baking the public base URL into the rendered preview.
  const base = `<base href="${window.location.origin}/">`;

  // Mail clients tolerate either a full document or a body fragment;
  // detect and pass through when the template already supplied a full
  // document, otherwise wrap in a minimal shell so the iframe has a
  // predictable rendering context.
  const trimmed = html.trim();
  if (/^<!doctype/i.test(trimmed) || /^<html[\s>]/i.test(trimmed)) {
    // Inject <base> as the first child of <head> so it precedes any image.
    if (/<head[^>]*>/i.test(trimmed)) {
      return trimmed.replace(/<head[^>]*>/i, (m) => `${m}${base}`);
    }
    // No <head> — splice one in right after the opening <html> tag.
    return trimmed.replace(/<html[^>]*>/i, (m) => `${m}<head>${base}</head>`);
  }
  return `<!DOCTYPE html><html><head>${base}<meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><style>body{margin:0;padding:16px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:#26272B;background:#ffffff;}</style></head><body>${html}</body></html>`;
}

function renderPreviewError(err: unknown, t: (k: string) => string): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case 'not_found':
        return t('settings.log.email.preview.error.notFound');
      case 'no_template':
        return t('settings.log.email.preview.error.noTemplate');
      case 'render_failed':
        return t('settings.log.email.preview.error.renderFailed');
      default:
        return t('settings.log.email.preview.error.unknown');
    }
  }
  return t('settings.log.email.preview.error.unknown');
}
